import React, { useEffect, useRef } from 'react';
import { siteData, stats } from '../data';

export default function Hero() {
  const countersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const animateCounter = (el: HTMLElement, target: number, suffix: string) => {
      let start = 0;
      const duration = 2000;
      const startTime = performance.now();
      const step = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.floor(eased * target) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll('[data-target]').forEach((counter) => {
            const el = counter as HTMLElement;
            const target = parseInt(el.dataset.target || '0');
            const suffix = el.dataset.suffix || '';
            animateCounter(el, target, suffix);
          });
          observer.disconnect();
        }
      });
    }, { threshold: 0.3 });

    if (countersRef.current) observer.observe(countersRef.current);
  }, []);

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-20" style={{background:'linear-gradient(135deg,#0A0A14 0%,#0F1629 50%,#0A0A14 100%)'}}>
      {/* Grid bg */}
      <div className="grid-bg absolute inset-0" />

      {/* Orbs */}
      <div className="orb w-96 h-96 -top-20 right-20 opacity-20" style={{background:'rgba(200,168,75,0.5)',animationDelay:'0s'}} />
      <div className="orb w-72 h-72 bottom-20 left-10 opacity-15" style={{background:'rgba(74,144,226,0.5)',animationDelay:'-3s'}} />
      <div className="orb w-48 h-48 top-1/2 right-1/3 opacity-10" style={{background:'rgba(200,168,75,0.4)',animationDelay:'-1.5s'}} />

      {/* Diagonal line accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-px h-full opacity-10" style={{background:'linear-gradient(to bottom,transparent,#C8A84B,transparent)'}} />
        <div className="absolute top-0 left-1/2 w-px h-full opacity-5" style={{background:'linear-gradient(to bottom,transparent,#C8A84B,transparent)'}} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-3 px-4 py-2 mb-8 rounded-full border" style={{borderColor:'rgba(200,168,75,0.3)',background:'rgba(200,168,75,0.06)'}}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{background:'#C8A84B'}} />
              <span className="text-xs tracking-[3px] uppercase" style={{color:'#C8A84B',fontFamily:'Space Mono,monospace'}}>
                Established {siteData.established} · Saharanpur
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-playfair font-black leading-[1.05] mb-6" style={{fontFamily:'Playfair Display,serif',fontSize:'clamp(2.8rem,5vw,5rem)'}}>
              <span className="text-white block">Shape Your</span>
              <span className="shimmer-text block">Digital Future</span>
              <span className="text-white block">With Confidence</span>
            </h1>

            <p className="text-base leading-relaxed mb-10 max-w-xl" style={{color:'rgba(212,207,197,0.7)'}}>
              ITS Academy — Saharanpur's most trusted computer education centre with{' '}
              <span style={{color:'#C8A84B'}}>24+ years of excellence</span>,{' '}
              <span style={{color:'#C8A84B'}}>400+ courses</span>, and{' '}
              <span style={{color:'#C8A84B'}}>1,500+ satisfied students</span>.
              NIELIT affiliated · Government recognized.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button onClick={() => document.querySelector('#courses')?.scrollIntoView({behavior:'smooth'})} className="btn-gold text-sm tracking-widest uppercase font-bold">
                Explore Courses →
              </button>
              <button onClick={() => document.querySelector('#contact')?.scrollIntoView({behavior:'smooth'})} className="btn-outline-gold text-sm tracking-widest uppercase">
                Enroll Today
              </button>
            </div>

            {/* Rating row */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex gap-1">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5" fill="#C8A84B" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className="font-bold text-white">4.9/5</span>
              <span style={{color:'rgba(212,207,197,0.5)',fontSize:'0.82rem'}}>from 1500+ verified reviews</span>
            </div>
          </div>

          {/* Right - Visual Card */}
          <div className="relative hidden lg:block">
            {/* Main visual */}
            <div className="relative rounded-sm overflow-hidden" style={{height:'480px'}}>
              {/* <div className="img-placeholder w-full h-full" style={{minHeight:'480px'}}>
                <div className="text-5xl mb-3">🏫</div>
                <span style={{color:'rgba(200,168,75,0.4)',fontSize:'0.75rem',letterSpacing:'2px'}}>ITS ACADEMY CAMPUS</span>
                <span style={{color:'rgba(212,207,197,0.3)',fontSize:'0.7rem'}}>Place logo.png here</span>
              </div> */}
               <img src="/images/logos/mainlogo.png" className="w-full h-full object-contain" alt="ITS Academy Logo" />

              {/* Floating info card */}
              <div className="absolute bottom-6 left-2 right-10 glass-card rounded-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center text-black font-black text-sm" style={{background:'#C8A84B'}}>
                    ITS
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">ITS Academy</div>
                    <div style={{color:'rgba(212,207,197,0.5)',fontSize:'0.72rem'}}>Saharanpur's #1 Computer Institute</div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="font-bold" style={{color:'#C8A84B',fontFamily:'Playfair Display,serif',fontSize:'1.3rem'}}>4.9★</div>
                    <div style={{color:'rgba(212,207,197,0.4)',fontSize:'0.65rem'}}>1500+ Reviews</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats card */}
            <div className="absolute -bottom-6 -right-6 glass-card rounded-sm p-5" style={{borderColor:'rgba(200,168,75,0.2)'}}>
              <div className="grid grid-cols-2 gap-4">
                {stats.map(stat => (
                  <div key={stat.label} className="text-center">
                    <div className="font-black leading-none mb-1" style={{fontFamily:'Playfair Display,serif',fontSize:'1.8rem',color:'#C8A84B'}}>
                      {stat.value}{stat.suffix}
                    </div>
                    <div style={{fontSize:'0.65rem',letterSpacing:'1px',textTransform:'uppercase',color:'rgba(212,207,197,0.4)'}}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile stats */}
        <div ref={countersRef} className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 lg:mt-20 pt-12 border-t" style={{borderColor:'rgba(200,168,75,0.1)'}}>
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-black leading-none mb-2" style={{fontFamily:'Playfair Display,serif',fontSize:'clamp(2rem,4vw,3rem)',color:'#C8A84B'}}>
                <span data-target={stat.value} data-suffix={stat.suffix}>0{stat.suffix}</span>
              </div>
              <div style={{fontSize:'0.72rem',letterSpacing:'2px',textTransform:'uppercase',color:'rgba(212,207,197,0.45)'}}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-12 flex items-center gap-3" style={{color:'rgba(212,207,197,0.35)',fontSize:'0.7rem',letterSpacing:'3px',textTransform:'uppercase'}}>
        <div className="scroll-line" />
        Scroll to explore
      </div>
    </section>
  );
}
